import * as THREE from 'three/webgpu';
import { Observable, Subject, type Subscription } from 'rxjs';
//------ 右键删除策略分层 开始------
import { SketchContextMenuDeleteHandler } from './context-menu-delete/index';
//------ 右键删除策略分层 结束------
import type { Viewer } from '../Viewer';
import { ViewerMode } from '../ViewerMode';
import {RawPointerEventType, type RawPointerEventPayload} from './EventManager';
//------ 特征面草图绘制 开始------
import { isFeatureFace } from '../../mesh/feature';
import { SketchFaceViewFrameBuilder } from '../../sketch/face';
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata';
//------ 特征面草图绘制 结束------

/**
 * 右键菜单动作枚举。
 */
export enum ContextMenuActionType {
    /** 删除选中对象。 */
    Delete = 'delete',
    //------ 平面目标草图绘制 开始------
    /** 在选中平面面或正式基准面上插入草图。 */
    InsertSketch = 'insert-sketch',
    /** 正视于选中的平面面或正式基准面。 */
    NormalToFace = 'normal-to-face',
    //------ 平面目标草图绘制 结束------
    /** 重新打开当前文档的剖面视图属性。 */
    SectionViewProperties = 'section-view-properties',
}

/**
 * 右键菜单作用域枚举。
 */
export enum ContextMenuScopeType {
    /** 非草图作用域。 */
    None = 'none',
    /** 草图作用域。 */
    Sketch = 'sketch',
    /** 装配体作用域。 */
    Assembly = 'assembly',
    //------ 特征面草图绘制 开始------
    /** 特征(默认/装配体)作用域。 */
    Feature = 'feature',
    /** 装配体零部件作用域。 */
    AssemblyComponent = 'assembly-component',
    //------ 特征面草图绘制 结束------
}

//------ 右键拖拽菜单抑制 开始------
/**
 * 右键菜单鼠标按键枚举。
 */
enum ContextMenuMouseButton {
    /** 鼠标右键。 */
    Right = 2,
}

/**
 * 右键菜单鼠标按键位掩码枚举。
 */
enum ContextMenuMouseButtonsMask {
    /** 鼠标右键处于按下状态。 */
    Right = 2,
}

/**
 * 右键鼠标会话状态。
 */
interface ContextMenuRightMouseSession {
    /** 右键按下时的客户端 X 坐标。 */
    startClientX: number;
    /** 右键按下时的客户端 Y 坐标。 */
    startClientY: number;
    /** 当前右键会话是否已经移动超过拖拽阈值。 */
    hasMovedBeyondThreshold: boolean;
}
//------ 右键拖拽菜单抑制 结束------

/**
 * 右键菜单打开事件载荷。
 */
export interface ContextMenuOpenEventPayload {
    /** 事件类型。 */
    type: 'context-menu-open';
    /** 触发时鼠标客户端 X 坐标。 */
    clientX: number;
    /** 触发时鼠标客户端 Y 坐标。 */
    clientY: number;
    /** 触发时的菜单作用域。 */
    scope: ContextMenuScopeType;
    /** 当前上下文允许的菜单动作。 */
    actions: ContextMenuActionType[];
    /** 当前目标草图组 ID（兼容字段，当前删除逻辑不再依赖）。 */
    targetSketchGroupId: string | null;
    /** 当前选中对象 ID 列表。 */
    selectedObjectIds: string[];
    /** 当前选中对象快照列表。 */
    selectedObjects: THREE.Object3D[];
    /** 当前显式选中的草图智能尺寸 ID 列表。 */
    selectedSketchDimensionIds: string[];
    /** 当前选中对象对应的装配节点 ID 列表。 */
    selectedAssemblyNodeIds: string[];
    /** 右键菜单打开时主选正式基准面 ID；非基准面上下文为空。 */
    selectedDatumPlaneId: number | null;
    /** 事件时间戳。 */
    timestamp: number;
}



/**
 * 视口右键菜单控制器。
 *
 * 设计说明：
 * 1. 统一收口容器 `contextmenu` 事件，避免宿主层自行监听导致逻辑分散；
 * 2. 菜单可用动作在 cad-core 内按运行时上下文计算，宿主层只负责渲染；
 * 3. 动作执行入口也在控制器内，保证“判定逻辑”和“执行逻辑”一致。
 */
export class ContextMenuController {
    //------ 右键拖拽菜单抑制 开始------
    /** 右键拖拽判定阈值，单位为 CSS 像素。 */
    private static readonly RIGHT_MOUSE_DRAG_THRESHOLD_PX = 4;
    //------ 右键拖拽菜单抑制 结束------

    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 打开菜单事件流主题。 */
    private readonly openSubject: Subject<ContextMenuOpenEventPayload> = new Subject<ContextMenuOpenEventPayload>();

    /** 是否已启动。 */
    private started: boolean = false;

    /** 最近一次上下文快照。 */
    private latestContext: ContextMenuOpenEventPayload | null = null;

    //------ 右键删除策略分层 开始------
    /** 草图模式删除处理器。 */
    private readonly sketchDeleteHandler: SketchContextMenuDeleteHandler;
    //------ 右键删除策略分层 结束------

    //------ 特征面正视于 开始------
    /** 特征面正视相机帧构造器。 */
    private readonly sketchFaceViewFrameBuilder: SketchFaceViewFrameBuilder = new SketchFaceViewFrameBuilder();
    //------ 特征面正视于 结束------

    /** contextmenu 监听参数。 */
    private readonly contextMenuListenerOptions: AddEventListenerOptions = { capture: true };

    //------ 右键拖拽菜单抑制 开始------
    /** 原始鼠标事件订阅集合。 */
    private readonly rawPointerSubscriptions: Subscription[] = [];

    /** 当前右键鼠标会话。 */
    private rightMouseSession: ContextMenuRightMouseSession | null = null;

    /** 下一次 contextmenu 是否应被右键拖拽抑制。 */
    private shouldSuppressNextContextMenu: boolean = false;
    //------ 右键拖拽菜单抑制 结束------

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        //------ 右键删除策略分层 开始------
        this.sketchDeleteHandler = new SketchContextMenuDeleteHandler(viewer);
        //------ 右键删除策略分层 结束------
    }

    /**
     * 启动右键菜单监听。
     */
    public start(): void {
        if (this.started) {
            return;
        }
        const container = this.viewer.getContainer();
        if (!container) {
            return;
        }
        container.addEventListener('contextmenu', this.handleContextMenu, this.contextMenuListenerOptions);
        //------ 右键拖拽菜单抑制 开始------
        this.bindRawPointerSubscriptions();
        //------ 右键拖拽菜单抑制 结束------
        this.started = true;
    }

    /**
     * 停止右键菜单监听。
     */
    public stop(): void {
        if (!this.started) {
            return;
        }
        const container = this.viewer.getContainer();
        if (container) {
            container.removeEventListener('contextmenu', this.handleContextMenu, this.contextMenuListenerOptions);
        }
        //------ 右键拖拽菜单抑制 开始------
        this.unbindRawPointerSubscriptions();
        //------ 右键拖拽菜单抑制 结束------
        this.started = false;
    }

    /**
     * 释放控制器资源。
     */
    public dispose(): void {
        this.stop();
        this.latestContext = null;
        //------ 右键拖拽菜单抑制 开始------
        this.clearRightMouseSession();
        //------ 右键拖拽菜单抑制 结束------
        this.openSubject.complete();
    }

    /**
     * 观察右键菜单打开事件。
     *
     * @returns 菜单打开事件流。
     */
    public observeOpen(): Observable<ContextMenuOpenEventPayload> {
        return this.openSubject.asObservable();
    }

    /**
     * 执行右键菜单动作。
     *
     * @param action 目标动作。
     * @returns 动作执行是否成功。
     */
    public executeAction(action: ContextMenuActionType): boolean {
        //------ 右键菜单动作分发 开始------
        switch (action) {
            case ContextMenuActionType.Delete:
                return this.executeDeleteAction();
            //------ 平面目标正视于 开始------
            case ContextMenuActionType.NormalToFace:
                return this.executeNormalToAction();
            //------ 平面目标正视于 结束------
            case ContextMenuActionType.SectionViewProperties:
                if (!this.viewer.sectionViewController.getState().hasDefinition) {
                    return false;
                }
                this.viewer.sectionViewController.edit();
                return true;
            default:
                return false;
        }
        //------ 右键菜单动作分发 结束------
    }

    //------ Delete 键复用右键删除 开始------
    /**
     * 基于当前选择执行删除动作。
     *
     * 设计说明：
     * 1. Delete 键没有浏览器右键事件上下文，因此在 cad-core 内构造与右键菜单一致的上下文快照；
     * 2. 删除动作继续走 `executeAction(Delete)`，确保 Delete 键与右键菜单复用同一套删除分发和处理器；
     * 3. 不向菜单打开事件流派发载荷，避免键盘删除误触发宿主菜单 UI。
     *
     * @returns 删除动作是否执行成功。
     */
    public executeDeleteForCurrentSelection(): boolean {
        this.latestContext = this.buildCurrentSelectionPayload();
        return this.executeAction(ContextMenuActionType.Delete);
    }
    //------ Delete 键复用右键删除 结束------

    /**
     * 执行删除动作。
     *
     * @returns 动作执行是否成功。
     */
    private executeDeleteAction(): boolean {
        const context = this.latestContext;
        if (!context || !context.actions.includes(ContextMenuActionType.Delete)) {
            return false;
        }

        //------ 右键菜单上下文分发 开始------
        /**
         * 扩展说明：
         * 1. 当前分发基于 Viewer 运行时上下文模式；
         * 2. 仅实现草图模式删除，其他模式统一返回 false；
         * 3. 后续扩展新上下文时仅需新增 switch 分支。
         */
        const contextMode = this.resolveScopeType();
        switch (contextMode) {
            case ContextMenuScopeType.Sketch:
                return this.sketchDeleteHandler.execute(context);
            default:
                return false;
        }
        //------ 右键菜单上下文分发 结束------
    }

    //------ 平面目标正视于 开始------
    /**
     * 执行特征面或正式基准面的正视动作。
     *
     * 设计说明：
     * 1. 正视于只改变相机视图，属于 View 行为，不创建草图会话、不创建临时基准面、不写模型历史；
     * 2. 正式基准面复用 `Viewer.enterDatumPlaneView()`，与设计树正视和进入草图保持同一相机入口；
     * 3. 特征面继续复用草图进入时同一套 `SketchFaceViewFrameBuilder`，确保屏幕 up 与草图视角一致；
     * 4. 非平面面或失效基准面在这里直接返回 false，由宿主菜单层给出用户提示。
     *
     * @returns 正视动作是否执行成功。
     */
    private executeNormalToAction(): boolean {
        const context = this.latestContext;
        if (!context || !context.actions.includes(ContextMenuActionType.NormalToFace)) {
            return false;
        }

        if (context.scope !== ContextMenuScopeType.Feature) {
            return false;
        }

        const selectedDatumPlaneId = context.selectedDatumPlaneId;
        if (selectedDatumPlaneId !== null) {
            if (!this.viewer.getDatumPlaneSummary?.(selectedDatumPlaneId)) {
                return false;
            }
            this.viewer.enterDatumPlaneView(selectedDatumPlaneId);
            // 正视于仅改变相机可见状态，交给统一帧调度器合并同一宿主帧内的重复请求。
            this.viewer.requestRender();
            return true;
        }

        const face = this.viewer.getSelectedPlanarFace?.() ?? null;
        const camera = this.viewer.camera ?? null;
        const cameraManager = this.viewer.cameraManager ?? null;
        const container = this.viewer.getContainer();
        if (!face || !face.isPlanar || !camera || !cameraManager || !container) {
            return false;
        }

        const viewportAspect = container.clientHeight > 0
            ? container.clientWidth / container.clientHeight
            : 1;
        const viewFrame = this.sketchFaceViewFrameBuilder.build({
            face,
            camera,
            viewportAspect,
        });

        const faceBounds = new THREE.Box3().setFromPoints(face.worldVertices);
        if (faceBounds.isEmpty()) {
            cameraManager.lookAtTarget(
                viewFrame.cameraPosition.clone(),
                viewFrame.target.clone(),
                viewFrame.up.clone(),
            );
            // 空包围盒路径同样只需在下一宿主帧展示最终相机状态，无需同步重入渲染管线。
            this.viewer.requestRender();
            return true;
        }

        cameraManager.focusOnBounds(faceBounds, {
            direction: viewFrame.displayNormal.clone(),
            up: viewFrame.up.clone(),
        });
        return true;
    }
    //------ 平面目标正视于 结束------

    /**
     * 处理浏览器右键菜单事件。
     *
     * @param event 浏览器原生事件。
     */
    private readonly handleContextMenu = (event: MouseEvent): void => {
        // 统一阻止浏览器默认右键菜单，避免宿主与浏览器菜单并存。
        event.preventDefault();

        //------ 右键拖拽菜单抑制 开始------
        // 右键拖拽用于旋转视图，拖拽结束后的 contextmenu 只应被吞掉，不应打开业务菜单。
        if (this.shouldSuppressContextMenuForRightMouseDrag()) {
            this.clearRightMouseSession();
            return;
        }
        //------ 右键拖拽菜单抑制 结束------

        const payload = this.buildOpenPayload(event);
        this.latestContext = payload;
        this.openSubject.next(payload);
    };

    //------ 右键拖拽菜单抑制 开始------
    /**
     * 订阅事件中心派发的原始鼠标事件。
     */
    private bindRawPointerSubscriptions(): void {
        const mouseDownSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.MouseDown)
            ?.subscribe((payload: RawPointerEventPayload) => this.handleRawMouseDown(payload));
        const mouseMoveSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.MouseMove)
            ?.subscribe((payload: RawPointerEventPayload) => this.handleRawMouseMove(payload));
        const mouseUpSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.MouseUp)
            ?.subscribe((payload: RawPointerEventPayload) => this.handleRawMouseUp(payload));
        const pointerCancelSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.PointerCancel)
            ?.subscribe((payload: RawPointerEventPayload) => this.handleRawPointerCancel(payload));

        if (mouseDownSubscription) {
            this.rawPointerSubscriptions.push(mouseDownSubscription);
        }
        if (mouseMoveSubscription) {
            this.rawPointerSubscriptions.push(mouseMoveSubscription);
        }
        if (mouseUpSubscription) {
            this.rawPointerSubscriptions.push(mouseUpSubscription);
        }
        if (pointerCancelSubscription) {
            this.rawPointerSubscriptions.push(pointerCancelSubscription);
        }
    }

    /**
     * 取消订阅事件中心派发的原始鼠标事件。
     */
    private unbindRawPointerSubscriptions(): void {
        this.rawPointerSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.rawPointerSubscriptions.length = 0;
    }

    /**
     * 处理事件中心派发的鼠标按下事件。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleRawMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== ContextMenuMouseButton.Right) {
            return;
        }

        this.rightMouseSession = {
            startClientX: payload.clientX,
            startClientY: payload.clientY,
            hasMovedBeyondThreshold: false,
        };
        this.shouldSuppressNextContextMenu = false;
    }

    /**
     * 处理事件中心派发的鼠标移动事件。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleRawMouseMove(payload: RawPointerEventPayload): void {
        const session = this.rightMouseSession;
        if (!session) {
            return;
        }
        if ((payload.buttons & ContextMenuMouseButtonsMask.Right) !== ContextMenuMouseButtonsMask.Right) {
            return;
        }

        const deltaX = payload.clientX - session.startClientX;
        const deltaY = payload.clientY - session.startClientY;
        const squaredDistance = deltaX ** 2 + deltaY ** 2;
        const squaredThreshold = ContextMenuController.RIGHT_MOUSE_DRAG_THRESHOLD_PX ** 2;
        if (squaredDistance < squaredThreshold) {
            return;
        }

        session.hasMovedBeyondThreshold = true;
        this.shouldSuppressNextContextMenu = true;
    }

    /**
     * 处理事件中心派发的鼠标抬起事件。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleRawMouseUp(payload: RawPointerEventPayload): void {
        const session = this.rightMouseSession;
        if (!session || payload.button !== ContextMenuMouseButton.Right) {
            return;
        }

        this.shouldSuppressNextContextMenu = session.hasMovedBeyondThreshold;
        this.rightMouseSession = null;
    }

    /**
     * 处理右键会话取消。
     *
     * 设计说明：
     * 鼠标移出 canvas 后业务交互已经取消，后续浏览器 contextmenu 不应再打开装配节点菜单。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleRawPointerCancel(payload: RawPointerEventPayload): void {
        if (payload.button !== ContextMenuMouseButton.Right) {
            return;
        }
        this.rightMouseSession = null;
        this.shouldSuppressNextContextMenu = true;
    }

    /**
     * 判断当前 contextmenu 是否应因右键拖拽被抑制。
     *
     * @returns 应抑制时返回 true。
     */
    private shouldSuppressContextMenuForRightMouseDrag(): boolean {
        return this.shouldSuppressNextContextMenu
            || this.rightMouseSession?.hasMovedBeyondThreshold === true;
    }

    /**
     * 清理右键鼠标会话。
     */
    private clearRightMouseSession(): void {
        this.rightMouseSession = null;
        this.shouldSuppressNextContextMenu = false;
    }
    //------ 右键拖拽菜单抑制 结束------

    /**
     * 构建右键菜单打开载荷。
     *
     * @param event 浏览器原生事件。
     * @returns 菜单事件载荷。
     */
    private buildOpenPayload(event: MouseEvent): ContextMenuOpenEventPayload {
        const selectedObjects = this.viewer.getSelectedObjects();
        const selectedObjectIds = selectedObjects.map((object) => object.uuid);
        const selectedSketchDimensionIds = this.resolveSelectedSketchDimensionIds();
        const selectedAssemblyNodeIds = this.resolveSelectedAssemblyNodeIds(selectedObjects);
        const selectedDatumPlaneId = this.resolveSelectedDatumPlaneId();
        const scope = this.resolveScopeType(selectedDatumPlaneId);
        const targetSketchGroupId = this.resolveTargetSketchGroupId(selectedObjects);
        const actions = this.resolveAvailableActions(scope, targetSketchGroupId, selectedDatumPlaneId);

        return {
            type: 'context-menu-open',
            clientX: event.clientX,
            clientY: event.clientY,
            scope,
            actions,
            targetSketchGroupId,
            selectedObjectIds,
            selectedObjects,
            selectedSketchDimensionIds,
            selectedAssemblyNodeIds,
            selectedDatumPlaneId,
            timestamp: Date.now(),
        };
    }

    //------ Delete 键复用右键删除 开始------
    /**
     * 构建当前选择上下文载荷。
     *
     * 设计说明：
     * 1. 该载荷仅供键盘删除内部复用右键菜单删除处理器；
     * 2. 坐标字段不参与删除逻辑，统一填 0，避免引入无意义的宿主事件依赖；
     * 3. 动作列表仍由当前 Viewer 模式和选择状态计算，保持与右键菜单可用性判定一致。
     *
     * @returns 当前选择上下文载荷。
     */
    private buildCurrentSelectionPayload(): ContextMenuOpenEventPayload {
        const selectedObjects = this.viewer.getSelectedObjects();
        const selectedObjectIds = selectedObjects.map((object) => object.uuid);
        const selectedSketchDimensionIds = this.resolveSelectedSketchDimensionIds();
        const selectedAssemblyNodeIds = this.resolveSelectedAssemblyNodeIds(selectedObjects);
        const selectedDatumPlaneId = this.resolveSelectedDatumPlaneId();
        const scope = this.resolveScopeType(selectedDatumPlaneId);
        const targetSketchGroupId = this.resolveTargetSketchGroupId(selectedObjects);
        const actions = this.resolveAvailableActions(scope, targetSketchGroupId, selectedDatumPlaneId);

        return {
            type: 'context-menu-open',
            clientX: 0,
            clientY: 0,
            scope,
            actions,
            targetSketchGroupId,
            selectedObjectIds,
            selectedObjects,
            selectedSketchDimensionIds,
            selectedAssemblyNodeIds,
            selectedDatumPlaneId,
            timestamp: Date.now(),
        };
    }
    //------ Delete 键复用右键删除 结束------

    /**
     * 解析当前菜单作用域。
     *
     * @param selectedDatumPlaneId 当前上下文已经解析的正式基准面 ID。
     * @returns 作用域类型。
     */
    private resolveScopeType(
        selectedDatumPlaneId: number | null = this.resolveSelectedDatumPlaneId(),
    ): ContextMenuScopeType {
        const currentMode = this.viewer.getMode();
        // 正式基准面属于独立平面命令目标，优先级必须高于当前草图或装配交互模式。
        if (selectedDatumPlaneId !== null) {
            return ContextMenuScopeType.Feature;
        }
        // 草图模式
        if (currentMode === ViewerMode.Sketch) {
            return ContextMenuScopeType.Sketch;
        }
        if (currentMode === ViewerMode.Assembly || currentMode === ViewerMode.MoveComponent) {
            const selectedObjects = this.viewer.getSelectedObjects();
            const hasAssemblyNodeSelection = this.resolveSelectedAssemblyNodeIds(selectedObjects).length > 0;
            const hasImportedModelSelection = selectedObjects.some((object) => (
                ImportedModelObjectMetadataService.isImportedModelObject(object)
            ));
            if (hasAssemblyNodeSelection || hasImportedModelSelection) {
                return ContextMenuScopeType.AssemblyComponent;
            }
            if (currentMode === ViewerMode.Assembly) {
                return ContextMenuScopeType.Assembly;
            }
        }
        // 特征模式(Default) -> 可在选中面上插入草图
        if (currentMode === ViewerMode.Default) {
            return ContextMenuScopeType.Feature;
        }
        return ContextMenuScopeType.None;
    }

    /**
     * 判断当前 Viewer 模式是否允许从正式基准面创建新草图。
     *
     * 设计说明：
     * 1. 默认零件模式和基础装配模式允许创建草图；
     * 2. 活动草图、基准特征 PropertyManager、移动组件、测量等互斥命令只能执行正视于；
     * 3. 该白名单只约束“创建新草图”命令，不影响基准面选择或视图调整。
     *
     * @param mode 当前 Viewer 模式。
     * @returns 当前是否允许创建新草图。
     */
    private canCreateSketchFromDatumPlane(mode: ViewerMode): boolean {
        return mode === ViewerMode.Default || mode === ViewerMode.Assembly;
    }

    /**
     * 解析并验证当前主选正式基准面 ID。
     *
     * @returns 当前主选对应有效正式基准面时返回 ID，否则返回 null。
     */
    private resolveSelectedDatumPlaneId(): number | null {
        const planeId = this.viewer.getSelectedDatumPlaneId?.() ?? null;
        if (typeof planeId !== 'number') {
            return null;
        }
        return this.viewer.getDatumPlaneSummary?.(planeId) ? planeId : null;
    }

    /**
     * 解析当前上下文可用动作。
     *
     * @param scope 菜单作用域。
     * @param targetSketchGroupId 目标草图组 ID。
     * @param selectedDatumPlaneId 当前上下文已经解析的正式基准面 ID。
     * @returns 可用动作列表。
     */
    private resolveAvailableActions(
        scope: ContextMenuScopeType,
        targetSketchGroupId: string | null,
        selectedDatumPlaneId: number | null = this.resolveSelectedDatumPlaneId(),
    ): ContextMenuActionType[] {
        const selectedObjects = this.viewer.getSelectedObjects();
        const hasSelection = selectedObjects.length > 0
            || this.resolveSelectedSketchDimensionIds().length > 0
            || selectedDatumPlaneId !== null;
        const sectionViewActions = this.viewer.sectionViewController.getState().hasDefinition
            ? [ContextMenuActionType.SectionViewProperties]
            : [];

        //------ 平面目标草图绘制 开始------
        /**
         * 1. 特征模式下，选中正式基准面或 FeatureFace 时返回草图绘制与正视动作；
         * 2. 已有草图编辑会话时，正式基准面只保留正视，禁止嵌套创建第二个草图；
         * 3. FeatureFace 非平面过滤由宿主 workflow 负责（viewer.getSelectedPlanarFace 会判定 isPlanar）。
         */
        if (scope === ContextMenuScopeType.Feature && hasSelection) {
            if (selectedDatumPlaneId !== null) {
                // 活动草图或其它互斥命令中禁止创建新草图，但正视于仍是合法视图动作。
                return this.canCreateSketchFromDatumPlane(this.viewer.getMode())
                    ? [
                        ContextMenuActionType.InsertSketch,
                        ContextMenuActionType.NormalToFace,
                        ...sectionViewActions,
                    ]
                    : [ContextMenuActionType.NormalToFace, ...sectionViewActions];
            }
            const hasFeatureFace = selectedObjects.some((object) => isFeatureFace(object));
            if (hasFeatureFace) {
                return [
                    ContextMenuActionType.InsertSketch,
                    ContextMenuActionType.NormalToFace,
                    ...sectionViewActions,
                ];
            }
            return sectionViewActions;
        }
        //------ 平面目标草图绘制 结束------

        // 草图模式下的删除动作
        if (scope === ContextMenuScopeType.Sketch && hasSelection) {
            void targetSketchGroupId;
            return [ContextMenuActionType.Delete, ...sectionViewActions];
        }

        // 剖面视图属于文档级显示状态，不依赖当前实体选择，可在普通装配/空白图形区重新编辑。
        return sectionViewActions;
    }

    /**
     * 读取当前显式选中的草图智能尺寸身份。
     *
     * 尺寸标注属于独立领域聚合，不是 Three.js Object3D；右键和键盘删除上下文必须显式携带，
     * 才能让混合框选在同一事务内删除全部选中内容。
     *
     * @returns 去重后的智能尺寸持久 ID 列表。
     */
    private resolveSelectedSketchDimensionIds(): string[] {
        return [...new Set(
            this.viewer.getSelectedSketchDimensions?.().map((selection) => selection.dimensionId) ?? [],
        )];
    }

    /**
     * 从当前选中对象解析装配节点 ID。
     *
     * @param selectedObjects 当前选中对象列表。
     * @returns 去重后的装配节点 ID 列表。
     */
    private resolveSelectedAssemblyNodeIds(selectedObjects: THREE.Object3D[]): string[] {
        const assemblyNodeIds = new Set<string>();
        selectedObjects.forEach((object) => {
            const assemblyNodeId = ImportedModelObjectMetadataService
                .resolveAssemblyNodeIdFromSelfOrAncestors(object)
                ?.trim();
            if (assemblyNodeId) {
                assemblyNodeIds.add(assemblyNodeId);
            }
        });
        return Array.from(assemblyNodeIds);
    }

    /**
     * 从当前选中对象解析目标草图组 ID。
     *
     * @param selectedObjects 当前选中对象列表。
     * @returns 草图组 ID；无法解析时返回 null。
     */
    private resolveTargetSketchGroupId(selectedObjects: THREE.Object3D[]): string | null {
        const primarySelectedObject = selectedObjects[0] ?? null;
        if (!primarySelectedObject) {
            return null;
        }

        const ownerSketchGroup = this.findOwnerSketchGroup(primarySelectedObject);
        if (!ownerSketchGroup) {
            return null;
        }

        return ownerSketchGroup.uuid;
    }

    /**
     * 向上查找目标对象所属草图组。
     *
     * @param object 目标对象。
     * @returns 草图组对象；不存在时返回 null。
     */
    private findOwnerSketchGroup(object: THREE.Object3D): THREE.Object3D | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (Reflect.get(current, 'isSketchGroup') === true) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }
}
